/**
 * Pure helpers for the organization settings pages: who is on the members
 * table and in what order, what each person's monthly model allowance is, the
 * calendar month an allowance covers, and the geometry of the usage meters.
 *
 * Nothing here fetches or renders; the pages read these so the table, the
 * member sheet and the usage page cannot disagree about a figure.
 *
 * @module components/settings/orgMembers.logic
 */
import { normalizeOrganizationRole } from "@repo/shared/organizationRole";

import type { OrgTeamMember } from "../../state/orgProfile";
import { formatGbp } from "../../usage/usageFormat";

/** The API refuses anything above this; the allowance dialog says so before sending. */
export const ALLOWANCE_MAX_GBP = 1_000_000;

const ALLOWANCE_TOO_LARGE = `Keep the allowance at or under £${ALLOWANCE_MAX_GBP.toLocaleString("en-GB")}.`;

/**
 * A typed allowance: blank means the platform default, otherwise pounds
 * from 0 up to the API's maximum. Bounded before rounding: a huge input
 * would overflow to Infinity, which JSON sends as null — the very value
 * that means "restore the default".
 */
export function parseAllowanceInput(
  value: string,
): { ok: true; value: number | null } | { ok: false; reason: string } {
  const text = value.trim().replace(/^£/, "").replace(/,/g, "");
  if (text.length === 0) return { ok: true, value: null };
  const parsed = Number(text);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { ok: false, reason: "Enter an amount in pounds, or leave it blank for the default." };
  }
  if (parsed > ALLOWANCE_MAX_GBP) return { ok: false, reason: ALLOWANCE_TOO_LARGE };
  // The epsilon keeps `x.xx5` from rounding down on binary floats.
  const rounded = Math.round((parsed + Number.EPSILON) * 100) / 100;
  if (!Number.isFinite(rounded) || rounded > ALLOWANCE_MAX_GBP) {
    return { ok: false, reason: ALLOWANCE_TOO_LARGE };
  }
  return { ok: true, value: rounded };
}

/**
 * A custom allowance typed into the limits dialog: like `parseAllowanceInput`,
 * but blank is not "the default" here — choosing Standard is how you get that.
 */
export function parseCustomAllowance(
  value: string,
): { ok: true; value: number } | { ok: false; reason: string } {
  if (value.trim().replace(/^£/, "").length === 0) {
    return { ok: false, reason: "Enter an amount in pounds." };
  }
  const parsed = parseAllowanceInput(value);
  if (!parsed.ok) {
    return parsed.reason === ALLOWANCE_TOO_LARGE
      ? parsed
      : { ok: false, reason: "Enter an amount in pounds, like 120 or 120.50." };
  }
  return parsed.value === null
    ? { ok: false, reason: "Enter an amount in pounds." }
    : { ok: true, value: parsed.value };
}

/** "£120.00 a month · on-demand usage on" or "Standard allowance · on-demand usage off". */
export function describeMemberUsage(member: {
  readonly modelAllowanceGbp: number | null;
  readonly onDemandModelUsage: boolean;
}): string {
  const allowance =
    member.modelAllowanceGbp === null
      ? "Standard allowance"
      : `${formatGbp(member.modelAllowanceGbp)} a month`;
  const onDemand = member.onDemandModelUsage
    ? "on-demand usage on"
    : "on-demand usage off — conversations pause at the allowance";
  return `${allowance} · ${onDemand}`;
}

/** "Head of Compliance · Legal", or whichever half is known; null when neither is. */
export function memberPositionLabel(
  member: Pick<OrgTeamMember, "jobTitle" | "department">,
): string | null {
  const parts = [member.jobTitle, member.department]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part.length > 0);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** "14 Sept 2026" in the viewer's locale; null when the date is unknown or unreadable. */
export function memberJoinedDate(joinedAt: string | null, locale?: string): string | null {
  if (!joinedAt) return null;
  const date = new Date(joinedAt);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

/** "Joined 14 Sept 2026"; null when the date is unknown or unreadable. */
export function memberJoinedLabel(joinedAt: string | null, locale?: string): string | null {
  const date = memberJoinedDate(joinedAt, locale);
  return date === null ? null : `Joined ${date}`;
}

/** The person's display name, falling back to their address. */
export const memberDisplayName = (member: Pick<OrgTeamMember, "name" | "email">): string =>
  member.name?.trim() || member.email;

/**
 * `role` arrives as a plain string: the wire schema stays permissive so a
 * server older than the `organization_role` enum still decodes. Read it
 * through the same fold the database applies, never by raw comparison.
 */
export const isAdminMember = (member: Pick<OrgTeamMember, "role">): boolean =>
  normalizeOrganizationRole(member.role) === "admin";

export const memberRoleLabel = (member: Pick<OrgTeamMember, "role">): "Admin" | "Member" =>
  isAdminMember(member) ? "Admin" : "Member";

export const countAdmins = (team: ReadonlyArray<Pick<OrgTeamMember, "role">>): number =>
  team.filter(isAdminMember).length;

const normalizeQuery = (value: string): string =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/**
 * The members whose name, address, title, department or role contain every
 * word of the query, in their original order. A blank query keeps everyone.
 */
export function filterMembers<T extends OrgTeamMember>(
  team: ReadonlyArray<T>,
  query: string,
): ReadonlyArray<T> {
  const words = normalizeQuery(query).split(" ").filter((word) => word.length > 0);
  if (words.length === 0) return team;
  return team.filter((member) => {
    const haystack = normalizeQuery(
      [
        member.name ?? "",
        member.email,
        member.jobTitle ?? "",
        member.department ?? "",
        memberRoleLabel(member),
      ].join(" "),
    );
    return words.every((word) => haystack.includes(word));
  });
}

/**
 * Table order: the viewer first (the row people look for), then admins, then
 * everyone else, each alphabetical by display name.
 */
export function sortMembers<T extends OrgTeamMember>(
  team: ReadonlyArray<T>,
  viewerUserId: string | null,
): ReadonlyArray<T> {
  const rank = (member: T) =>
    member.id === viewerUserId ? 0 : isAdminMember(member) ? 1 : 2;
  return team.toSorted(
    (a, b) =>
      rank(a) - rank(b) ||
      memberDisplayName(a).localeCompare(memberDisplayName(b), undefined, { sensitivity: "base" }),
  );
}

/**
 * The calendar month an allowance covers. Allowances are counted in UTC
 * months on the server, so the period is too, whatever the viewer's zone.
 */
export interface AllowanceMonth {
  /** `YYYY-MM`. */
  readonly month: string;
  /** "October 2026". */
  readonly label: string;
  /** First day of the month, `YYYY-MM-DD`. */
  readonly sinceDay: string;
  /** Today, `YYYY-MM-DD`. */
  readonly untilDay: string;
  /** First day of next month, `YYYY-MM-DD`: when allowances reset. */
  readonly resetsOn: string;
  /** Whole days until the reset, at least 1. */
  readonly daysLeft: number;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export function allowanceMonth(now: Date = new Date()): AllowanceMonth {
  const year = now.getUTCFullYear();
  const monthIndex = now.getUTCMonth();
  const start = Date.UTC(year, monthIndex, 1);
  const next = Date.UTC(year, monthIndex + 1, 1);
  const today = Date.UTC(year, monthIndex, now.getUTCDate());
  return {
    month: isoDay(start).slice(0, 7),
    label: `${MONTH_NAMES[monthIndex]} ${year}`,
    sinceDay: isoDay(start),
    untilDay: isoDay(today),
    resetsOn: isoDay(next),
    daysLeft: Math.max(1, Math.round((next - today) / 86_400_000)),
  };
}

/**
 * The platform's standard allowance, when the page can know it. The client is
 * never told the default directly; the viewer's own allowance figure is it
 * whenever no custom amount applies to them (no row of theirs on the team, or
 * a row without one — an operator outside the organization reads the default).
 */
export function resolveStandardAllowance(
  team: ReadonlyArray<Pick<OrgTeamMember, "id" | "modelAllowanceGbp">>,
  viewerUserId: string | null,
  viewerSpend: { readonly includedGbp: number | null } | null,
): number | null {
  if (viewerSpend === null || viewerSpend.includedGbp === null || viewerSpend.includedGbp < 0) {
    return null;
  }
  const own = viewerUserId === null ? undefined : team.find((member) => member.id === viewerUserId);
  if (own !== undefined && own.modelAllowanceGbp !== null) return null;
  return viewerSpend.includedGbp;
}

/** What a member may use this month before on-demand billing; null when unknown. */
export const memberAllowanceGbp = (
  member: Pick<OrgTeamMember, "modelAllowanceGbp">,
  standardGbp: number | null,
): number | null => member.modelAllowanceGbp ?? standardGbp;

/** Every member's allowance together; null when any of them is unknown. */
export function totalIncludedGbp(
  team: ReadonlyArray<Pick<OrgTeamMember, "modelAllowanceGbp">>,
  standardGbp: number | null,
): number | null {
  let total = 0;
  for (const member of team) {
    const allowance = memberAllowanceGbp(member, standardGbp);
    if (allowance === null) return null;
    total += allowance;
  }
  return Math.round(total * 100) / 100;
}

/** "£90.00" (standard) or "£120.00"; "Standard" when the default is unknown. */
export const allowanceAmountLabel = (
  member: Pick<OrgTeamMember, "modelAllowanceGbp">,
  standardGbp: number | null,
): string => {
  const allowance = memberAllowanceGbp(member, standardGbp);
  return allowance === null ? "Standard" : formatGbp(allowance);
};

export type UsageMeterTone = "normal" | "near" | "over" | "blocked";

/**
 * A usage meter as shares of its full width. Within the allowance the track
 * is the allowance and the fill is the spend; past it the whole bar re-scales
 * to the spend, so the allowance keeps a visible boundary and the excess gets
 * its own segment.
 */
export interface UsageMeterGeometry {
  /** Share of the bar the allowance occupies (1 while within it). */
  readonly includedShare: number;
  /** Share of the allowance used, 0–1. */
  readonly usedShare: number;
  /** Share of the bar the excess occupies (0 while within the allowance). */
  readonly overShare: number;
  readonly tone: UsageMeterTone;
}

/** From this share of the allowance on, the meter warns. */
export const NEAR_ALLOWANCE_SHARE = 0.9;

export function usageMeterGeometry(
  spentGbp: number,
  includedGbp: number,
  options: { readonly blocked?: boolean } = {},
): UsageMeterGeometry {
  const spent = Number.isFinite(spentGbp) ? Math.max(0, spentGbp) : 0;
  const included = Number.isFinite(includedGbp) ? Math.max(0, includedGbp) : 0;
  if (spent <= included) {
    const usedShare = included === 0 ? 0 : spent / included;
    return {
      includedShare: 1,
      usedShare,
      overShare: 0,
      tone: options.blocked ? "blocked" : usedShare >= NEAR_ALLOWANCE_SHARE ? "near" : "normal",
    };
  }
  const includedShare = included / spent;
  return {
    includedShare,
    usedShare: 1,
    overShare: 1 - includedShare,
    tone: options.blocked ? "blocked" : "over",
  };
}

/** "£45.10 of £90.00" — the spend figure every meter caption leads with. */
export const spendOfAllowanceLabel = (spentGbp: number, includedGbp: number): string =>
  `${formatGbp(spentGbp)} of ${formatGbp(includedGbp)}`;
